import { useState, useCallback, useMemo } from 'react'
import {
  Modal as AntModal,
  Button,
  Form as AntForm,
  Row,
  notification,
  Typography,
  Switch,
  Radio,
  Input
} from 'antd'
import { useForm, useFieldArray, FormProvider } from 'react-hook-form'
import { useNavigate, generatePath } from 'react-router-dom'
import * as ROUTES from 'admin/routes'
import { useApi, useApiQuery } from 'admin/lib/Api'
import { Guid } from 'admin/lib/guid'
import { Section, SectionProvider, FormItem, Select } from 'admin/lib/Forms'
import { BodyContentColumn } from 'admin/lib/Layouts'
import Password from 'antd/lib/input/Password'
import { PROVIDER_DEFINITION_ID } from '@qflow/constants'
import {
  validateEmailLocalPart,
  validateEmailNotInUse
} from './connectionValidation'
import {
  BLANK_CONNECTION,
  BLANK_EMAIL,
  MAX_EMAIL_CONNECTIONS,
  PROVIDER_OPTIONS,
  MODULE_OPTIONS,
  DEFAULT_EMAIL_NAME,
  DEFAULT_MOBILE_NAME,
  EMAIL_MODE_NEW,
  EMAIL_MODE_EXISTING,
  EMAIL_DOMAIN,
  EMAIL_PREFIX_DELIVERIES,
  EMAIL_PREFIX_WASTE
} from './connectionConstants'
import type { EmailMode } from './connectionConstants'
import {
  assembleDraftItem,
  prepareSubmissionData,
  createNewEmailConnection,
  createExistingEmailConnection,
  createEmailSubmissionData,
  showErrorModal
} from './connectionHelpers'
import type { EditConnectionProps, ConnectionFormData } from './connectionTypes'

export function EditConnection({
  connection,
  instruments = [],
  isNew,
  accountId,
  projectId
}: EditConnectionProps) {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  const allInstruments = useApiQuery(
    useCallback(api => {
      return api.getAllConnectionInstruments()
    }, [])
  )

  const allEmailAddresses = useMemo(
    () =>
      (allInstruments.data as unknown as any[])
        ?.filter(x => x.projectId !== projectId)
        .map(x => x.email) ?? [],
    [allInstruments.data, projectId]
  )

  const allEmailIdentifiers = useMemo(
    () =>
      (allInstruments.data as unknown as any[])
        ?.filter(x => x.projectId !== projectId)
        .map(x => x.emailIdentifier) ?? [],
    [allInstruments.data, projectId]
  )

  //
  // Form setup & values
  const draftItem = isNew
    ? BLANK_CONNECTION
    : connection
      ? assembleDraftItem(connection, instruments)
      : BLANK_CONNECTION

  const form = useForm<ConnectionFormData>({
    defaultValues: draftItem,
    mode: 'all',
    criteriaMode: 'firstError'
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'emails'
  })

  const [isEmailForm, setIsEmailForm] = useState(
    draftItem.providerDefinitionId === PROVIDER_DEFINITION_ID.EMAIL_VALUE
  )

  const [emailMode, setEmailMode] = useState<EmailMode>(EMAIL_MODE_NEW)

  //
  // Callbacks

  const api = useApi()

  const proceedWithSubmission = useCallback(
    async (data: ConnectionFormData) => {
      if (!api) return

      setSubmitting(true)

      const submissionData = prepareSubmissionData(draftItem, data, isEmailForm)

      let status: number
      let error: string | null

      if (isNew) {
        if (isEmailForm) {
          if (emailMode === EMAIL_MODE_NEW) {
            ;[, status, error] = await createNewEmailConnection(
              api,
              accountId,
              projectId,
              data
            )
          } else {
            ;[, status, error] = await createExistingEmailConnection(
              api,
              accountId,
              projectId,
              submissionData,
              data
            )
          }
        } else {
          ;[, status, error] = await api.registerConnection(
            accountId,
            projectId,
            submissionData as any
          )
        }
      } else {
        if (isEmailForm) {
          ;[, status, error] = await api.updateConnection(
            accountId,
            projectId,
            connection?.connectionId ?? '',
            submissionData as any
          )
          if (!error) {
            const emailSubmissionData = createEmailSubmissionData(
              connection?.connectionId ?? '',
              projectId,
              data
            )

            if (!emailSubmissionData) {
              setSubmitting(false)
              return
            }

            if (emailSubmissionData.length > 0) {
              ;[, status, error] = await api.updateConnectionInstruments(
                accountId,
                projectId,
                connection?.connectionId ?? '',
                emailSubmissionData
              )
            }
          }
        } else {
          ;[, status, error] = await api.updateConnection(
            accountId,
            projectId,
            connection?.connectionId ?? '',
            submissionData as any
          )
        }
      }

      if (error) {
        showErrorModal(status, error)
        setSubmitting(false)
      } else {
        setSubmitting(false)
        navigate(
          generatePath(ROUTES.CONNECTIONS.LIST, {
            accountId: accountId,
            projectId: projectId
          })
        )
        notification.success({
          message: 'Successfully saved connection'
        })
      }
    },
    [
      accountId,
      api,
      connection,
      draftItem,
      isEmailForm,
      isNew,
      navigate,
      projectId,
      emailMode
    ]
  )

  function handleProviderDefinitionIdChange(value: string) {
    form.setValue('providerDefinitionId', value)
    if (
      value === PROVIDER_DEFINITION_ID.EMAIL_VALUE &&
      form.getValues('name') === DEFAULT_MOBILE_NAME
    ) {
      form.setValue('name', DEFAULT_EMAIL_NAME)
    } else if (
      value !== PROVIDER_DEFINITION_ID.EMAIL_VALUE &&
      form.getValues('name') === DEFAULT_EMAIL_NAME
    ) {
      form.setValue('name', DEFAULT_MOBILE_NAME)
    }
    setIsEmailForm(value === PROVIDER_DEFINITION_ID.EMAIL_VALUE)
  }

  const onSubmit = useCallback(
    async (data: ConnectionFormData) => {
      // Show confirmation modal for new email setup
      if (isNew && isEmailForm && emailMode === EMAIL_MODE_NEW) {
        const deliveryEmail = `${EMAIL_PREFIX_DELIVERIES}.${data.sharedMailboxName}${EMAIL_DOMAIN}`
        const wasteEmail = `${EMAIL_PREFIX_WASTE}.${data.sharedMailboxName}${EMAIL_DOMAIN}`

        return new Promise<void>(resolve => {
          AntModal.confirm({
            title: 'Confirm New Email Connection',
            content: (
              <>
                <p>
                  You are about to create a new email connection with the
                  following email addresses:
                </p>
                <ul>
                  <li>
                    <strong>Delivery:</strong> {deliveryEmail}
                  </li>
                  <li>
                    <strong>Waste:</strong> {wasteEmail}
                  </li>
                </ul>
                <p>Please confirm that these email addresses are correct.</p>
              </>
            ),
            onOk: async () => {
              notification.info({
                message: 'Creating Email Connection',
                description:
                  'Please wait while we create your new email connection. This may take a few moments.',
                duration: 4
              })
              await proceedWithSubmission(data)
              resolve()
            },
            onCancel: () => {
              setSubmitting(false)
              resolve()
            },
            okText: 'Confirm and Create',
            cancelText: 'Cancel'
          })
        })
      } else {
        // For non-new email modes, proceed directly
        await proceedWithSubmission(data)
      }
    },
    [isNew, isEmailForm, emailMode, proceedWithSubmission]
  )

  const handleSubmit = form.handleSubmit(onSubmit)

  return (
    <FormProvider {...form}>
      <SectionProvider>
        <BodyContentColumn>
          <AntForm onFinish={handleSubmit} layout="vertical">
            <Section onKeyDown={() => {}} colour="primary">
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={3}>
                    {isNew ? 'Create New Connection' : 'Edit Connection'}
                  </Typography.Title>
                  <FormItem
                    name="name"
                    label="Connection Name"
                    inputRef={firstInputRef}
                    rules={{
                      required: true,
                      minLength: 5
                    }}
                  />
                  <FormItem
                    name="providerDefinitionId"
                    label="Provider Definition Id"
                    rules={{
                      required: true
                    }}
                    disabled={!isNew}
                  >
                    <Select
                      onSelect={handleProviderDefinitionIdChange}
                      options={PROVIDER_OPTIONS}
                    />
                  </FormItem>
                  <FormItem
                    name="connUsername"
                    label="User Name"
                    hidden={isEmailForm}
                    rules={{
                      required: !isEmailForm
                    }}
                    disabled={!isNew}
                  />
                  <FormItem
                    name="connPassword"
                    label="Password"
                    hidden={isEmailForm}
                    rules={{
                      required: !isEmailForm
                    }}
                    disabled={!isNew}
                  >
                    <Password />
                  </FormItem>
                </>
              )}
            </Section>
            {isNew && isEmailForm && (
              <Section onKeyDown={() => {}} colour="primary">
                {() => (
                  <>
                    <Typography.Title level={3}>
                      Email Configuration
                    </Typography.Title>
                    <AntForm.Item label="Select 'New' to create a new inbox, or select 'Existing' if the email inbox has already been created.">
                      <Radio.Group
                        value={emailMode}
                        onChange={e => setEmailMode(e.target.value)}
                      >
                        <Radio.Button value={EMAIL_MODE_NEW}>New</Radio.Button>
                        <Radio.Button value={EMAIL_MODE_EXISTING}>
                          Existing
                        </Radio.Button>
                      </Radio.Group>
                    </AntForm.Item>
                  </>
                )}
              </Section>
            )}
            {isEmailForm && emailMode === EMAIL_MODE_NEW && isNew && (
              <Section onKeyDown={() => {}} colour="primary">
                {({ firstInputRef }) => (
                  <>
                    <Typography.Title level={3}>
                      New Email Setup
                    </Typography.Title>
                    <FormItem
                      label="Shared Mailbox Name"
                      name="sharedMailboxName"
                      rules={{
                        required: true,
                        validate: {
                          checkValue: (v: string) => {
                            const formatResult = validateEmailLocalPart(v)
                            if (formatResult !== true) return formatResult

                            // Check both deliveries and waste emails are not in use
                            const deliveryCheck = validateEmailNotInUse(
                              v,
                              EMAIL_PREFIX_DELIVERIES,
                              allEmailAddresses
                            )
                            if (deliveryCheck !== true) return deliveryCheck

                            return validateEmailNotInUse(
                              v,
                              EMAIL_PREFIX_WASTE,
                              allEmailAddresses
                            )
                          }
                        }
                      }}
                    >
                      <Input placeholder="clientname" />
                    </FormItem>
                    <Typography.Text type="secondary">
                      This will create both {EMAIL_PREFIX_DELIVERIES}.{'{'}name
                      {'}'}
                      {EMAIL_DOMAIN} and {EMAIL_PREFIX_WASTE}.{'{'}name{'}'}
                      {EMAIL_DOMAIN} email addresses
                    </Typography.Text>
                  </>
                )}
              </Section>
            )}
            {fields.map((email, index) => (
              <div key={email.id}>
                {!isEmailForm ||
                (emailMode === EMAIL_MODE_NEW && isNew) ? null : (
                  <Section onKeyDown={() => {}} colour="primary">
                    {({ firstInputRef }) => (
                      <>
                        <Typography.Title level={3}>Email</Typography.Title>
                        {email.instrumentId === Guid.Empty && index !== 0 && (
                          <Button
                            type="primary"
                            disabled={submitting}
                            onClick={() => remove(index)}
                          >
                            {'Remove'}
                          </Button>
                        )}
                        <FormItem
                          name={`emails.${index}.instrumentId`}
                          defaultValue={email.instrumentId}
                          hidden={true}
                        />
                        <FormItem
                          name={`emails.${index}.feedId`}
                          defaultValue={email.feedId}
                          hidden={true}
                        />
                        {email.instrumentId !== Guid.Empty && (
                          <FormItem
                            name={`emails.${index}.isDeleted`}
                            label="Delete Email?"
                            defaultValue={false}
                          >
                            <Switch style={{ marginLeft: '1rem' }} />
                          </FormItem>
                        )}
                        <FormItem
                          name={`emails.${index}.email`}
                          label="Email"
                          inputRef={firstInputRef}
                          defaultValue={email.email || EMAIL_DOMAIN}
                          disabled={!isNew}
                          rules={{
                            required:
                              isEmailForm && emailMode !== EMAIL_MODE_NEW,
                            validate:
                              isEmailForm && emailMode !== EMAIL_MODE_NEW
                                ? {
                                    checkValue: (v: string) =>
                                      v.endsWith(EMAIL_DOMAIN) ||
                                      `ERROR: Email must end with ${EMAIL_DOMAIN}`,
                                    inUse: (v: string) =>
                                      !allEmailAddresses.includes(v) ||
                                      'Error: This email address is already in use.'
                                  }
                                : { checkValue: () => true }
                          }}
                        />
                        <FormItem
                          name={`emails.${index}.emailIdentifier`}
                          label="Email Identifier"
                          defaultValue={email.emailIdentifier}
                          rules={{
                            required:
                              isEmailForm && emailMode !== EMAIL_MODE_NEW,
                            pattern:
                              isEmailForm && emailMode !== EMAIL_MODE_NEW
                                ? {
                                    value:
                                      /^[{(]?[0-9A-Fa-f]{8}-([0-9A-Fa-f]{4}-){3}[0-9A-Fa-f]{12}[)}]?$/,
                                    message: 'Invalid'
                                  }
                                : undefined,
                            validate:
                              isEmailForm && emailMode !== EMAIL_MODE_NEW
                                ? {
                                    // checkValue is required and runs on page load
                                    // so we set this to always return true.
                                    checkValue: () => true,
                                    inUse: (v: string) =>
                                      !allEmailIdentifiers.includes(v) ||
                                      'Error: This email identifier is already in use.'
                                  }
                                : { checkValue: () => true }
                          }}
                        />
                        <FormItem
                          name={`emails.${index}.moduleTagDefinitionId`}
                          label="Module"
                          defaultValue={email.moduleTagDefinitionId}
                          rules={{
                            required:
                              isEmailForm && emailMode !== EMAIL_MODE_NEW
                          }}
                        >
                          <Select showSearch options={MODULE_OPTIONS} />
                        </FormItem>
                        {/** Never changed by DAs */}
                        <FormItem
                          name={`emails.${index}.permittedFileTypes`}
                          label="Permitted File Types"
                          defaultValue={email.permittedFileTypes}
                          hidden={true}
                        />
                        {/** Never changed by DAs */}
                        <FormItem
                          name={`emails.${index}.permittedSendersPattern`}
                          label="Permitted Sender Pattern"
                          hidden={true}
                          defaultValue={email.permittedSendersPattern}
                        />
                      </>
                    )}
                  </Section>
                )}
              </div>
            ))}
            {(isEmailForm || (!isEmailForm && isNew)) && (
              <Row
                align="middle"
                justify={
                  isEmailForm && emailMode === EMAIL_MODE_EXISTING
                    ? 'space-between'
                    : 'center'
                }
              >
                <Button type="primary" htmlType="submit" disabled={submitting}>
                  {isNew ? 'Create' : 'Update'}
                </Button>
                <Button
                  hidden={!isEmailForm || emailMode === EMAIL_MODE_NEW}
                  type="primary"
                  disabled={
                    submitting || fields.length >= MAX_EMAIL_CONNECTIONS
                  }
                  onClick={() => append(BLANK_EMAIL)}
                >
                  {'Add Email'}
                </Button>
              </Row>
            )}
          </AntForm>
        </BodyContentColumn>
      </SectionProvider>
    </FormProvider>
  )
}
